/* eslint-disable max-lines */
import { lazy } from 'react'
import { ConfigManager, COUNTRY } from '~/managers/ConfigManager'
import { PATHS } from './paths'
import { Route, PERMISSIONS } from '~/types'
import withBreadcrumbBar from '~/common/withBreadcrumbBar'
import { getDisbursementFeatureName } from '~/helpers/helpers'

const LandingPage = lazy(() =>
  import(/* webpackChunkName: "LandingPage" */ '~/biz/pages/LandingPage')
)

const NotFound = lazy(() => import(/* webpackChunkName: "NotFound" */ '~/biz/pages/NotFound'))

const Error = lazy(() => import(/* webpackChunkName: "Error" */ '~/biz/pages/Error'))

const Transactions = lazy(() =>
  import(/* webpackChunkName: "Transactions" */ '~/biz/pages/Transactions')
)
const Disbursements = lazy(() =>
  import(/* webpackChunkName: "Disbursements" */ '~/biz/pages/Disbursements')
)
const TransactionDetails = lazy(() =>
  import(/* webpackChunkName: "TransactionDetails" */ '~/biz/pages/TransactionDetails')
)
const SimulateDeposit = lazy(() =>
  import(/* webpackChunkName: "SimulateDeposit" */ '~/biz/pages/SimulateDeposit')
)
const Deposit = lazy(() => import(/* webpackChunkName: "Deposit" */ '~/biz/pages/Deposit'))
const DepositRequests = lazy(() =>
  import(/* webpackChunkName: "DepositRequests" */ '~/biz/pages/DepositRequests')
)
const MyAccount = lazy(() => import(/* webpackChunkName: "MyAccount" */ '~/biz/pages/MyAccount'))
const Withdraw = lazy(() => import(/* webpackChunkName: "Withdraw" */ '~/biz/pages/Withdraw'))
const DeveloperTools = lazy(() =>
  import(/* webpackChunkName: "DeveloperTools" */ '~/biz/pages/DeveloperTools')
)
const Team = lazy(() => import(/* webpackChunkName: "Team" */ '~/biz/pages/Team'))

const Identification = lazy(() => import('~/biz/pages/Identification'))

const Invoicing = lazy(() => import(/* webpackChunkName: "Invoicing" */ '~/biz/pages/Invoicing'))

const InvoicingDetails = lazy(() =>
  import(/* webpackChunkName: "InvoicingDetails" */ '~/biz/pages/InvoicingDetails')
)

const NewVerification = lazy(() =>
  import(/* webpackChunkName: "NewVerification" */ '~/biz/pages/NewVerification')
)

const VerificationFlow = lazy(() =>
  import(/* webpackChunkName: "VerificationFlow" */ '~/biz/pages/VerificationFlow')
)

const VerificationMyInfoCallback = lazy(() =>
  import(/* webpackChunkName: "NewVerification" */ '~/biz/pages/NewVerification/MyInfoDetails')
)
const VerificationIndo = lazy(() =>
  import(/* webpackChunkName: "VerificationIndo" */ '~/biz/pages/VerificationIndo')
)
const MerchantUser = lazy(() =>
  import(/* webpackChunkName: "MerchantUser" */ '~/biz/pages/MerchantUser')
)
const MerchantUserDetails = lazy(() =>
  import(/* webpackChunkName: "MerchantUserDetails" */ '~/biz/pages/MerchantUserDetails')
)
const InternalTools = lazy(() =>
  import(/* webpackChunkName: "InternalTools" */ '~/biz/pages/InternalTools')
)

const BizKycInformation = lazy(() =>
  import(
    /* webpackChunkName: "BizKycInformation" */ '~/biz/pages/BusinessVerification/BusinessInformations'
  )
)
const BizKycIntendedRelations = lazy(() =>
  import(
    /* webpackChunkName: "BizKycIntendedRelations" */ '~/biz/pages/BusinessVerification/IntendedRelations'
  )
)
const BizKycBusinessActivities = lazy(() =>
  import(
    /* webpackChunkName: "BizKycBusinessActivities" */ '~/biz/pages/BusinessVerification/BusinessActivities'
  )
)
const BizKycUploadDocuments = lazy(() =>
  import(
    /* webpackChunkName: "BizKycUploadDocuments" */ '~/biz/pages/BusinessVerification/UploadDocuments'
  )
)
const BizKycAccountManager = lazy(() =>
  import(
    /* webpackChunkName: "BizKycAccountManager" */ '~/biz/pages/BusinessVerification/AccountManager'
  )
)
const BizKycDirectorsAndBo = lazy(() =>
  import(
    /* webpackChunkName: "BizKycDirectorsAndBo" */ '~/biz/pages/BusinessVerification/DirectorsAndBo'
  )
)
const BizKycReview = lazy(() =>
  import(/* webpackChunkName: "BizKycReview" */ '~/biz/pages/BusinessVerification/Review')
)
const BizKycComplete = lazy(() =>
  import(/* webpackChunkName: "BizKycComplete" */ '~/biz/pages/BusinessVerification/Complete')
)

const XfersAccept = lazy(() =>
  import(/* webpackChunkName: "IndoTransactions" */ '~/biz/pages/IndoTransactions')
)

const CreditCardsPayments = lazy(() =>
  import(/* webpackChunkName: "CreditCardsPayments" */ '~/biz/pages/CreditCards/Payments')
)

const CreditCardsDisputes = lazy(() =>
  import(/* webpackChunkName: "CreditCardsDisputes" */ '~/biz/pages/CreditCards/Disputes')
)

const RedirectToPayments = lazy(() =>
  import(/* webpackChunkName: "RedirectToPayments" */ '~/biz/pages/CreditCards/RedirectToPayments')
)

const XfersAcceptTransactionDetails = lazy(() =>
  import(
    /* webpackChunkName: "XfersAcceptTransactionDetails" */ '~/biz/pages/IndoTransactionDetails/XfersAccept'
  )
)

const CreditCardsPaymentDetails = lazy(() =>
  import(
    /* webpackChunkName: "CreditCardsPaymentDetails" */ '~/biz/pages/CreditCardsDetails/Payments'
  )
)

const CreditCardsDisputeDetails = lazy(() =>
  import(
    /* webpackChunkName: "CreditCardsDisputeDetails" */ '~/biz/pages/CreditCardsDetails/Disputes'
  )
)

const CreditCardsSubmitEvidence = lazy(() =>
  import(
    /* webpackChunkName: "CreditCardsSubmitEvidence" */ '~/biz/pages/CreditCardsDetails/SubmitEvidence'
  )
)

const transactionsSubRoutes = [
  {
    sideBar: 'Xfers Accept',
    breadcrumbName: 'Xfers Accept',
    path: PATHS.TRANSACTIONS_XFERS_ACCEPT,
  },
  {
    sideBar: 'All Transactions',
    breadcrumbName: 'All Transactions',
    path: PATHS.TRANSACTIONS,
  },
]

const creditCardsSubRoutes = [
  {
    sideBar: 'Payments',
    path: PATHS.CREDIT_CARDS_PAYMENTS,
  },
  {
    sideBar: 'Disputes',
    path: PATHS.CREDIT_CARDS_DISPUTES,
  },
]

const idSpecificRoutes: Route[] = [
  {
    path: PATHS.TRANSACTIONS_XFERS_ACCEPT,
    component: withBreadcrumbBar(XfersAccept),
    breadcrumbName: 'Xfers Accept',
    exact: true,
  },
  {
    path: PATHS.TRANSACTIONS_XFERS_ACCEPT_DETAILS,
    component: XfersAcceptTransactionDetails,
    exact: true,
  },
]

const creditCardsRoutes: Route[] = [
  {
    path: PATHS.CREDIT_CARDS_PAYMENTS,
    component: withBreadcrumbBar(CreditCardsPayments),
    breadcrumbName: 'Credit Cards / Payments',
    pageTitle: 'Payments',
    exact: true,
  },
  {
    path: PATHS.CREDIT_CARDS_DISPUTES,
    component: withBreadcrumbBar(CreditCardsDisputes),
    breadcrumbName: 'Credit Cards / Disputes',
    pageTitle: 'Disputes',
    exact: true,
  },
]

const baseRoutes: Route[] = [
  {
    path: PATHS.LANDING_PAGE,
    component: LandingPage,
    exact: true,
    sideBar: 'Dashboard',
    breadcrumbName: 'Home',
  },
  {
    path: PATHS.TRANSACTIONS,
    component: withBreadcrumbBar(Transactions),
    permission: PERMISSIONS.TRANSACTION,
    exact: true,
    sideBar: 'Transactions',
    breadcrumbName: 'Transactions',
    subRoutes: ConfigManager.getCountry() === COUNTRY.INDONESIA ? transactionsSubRoutes : undefined,
  },
  {
    path: PATHS.CREDIT_CARDS,
    component: RedirectToPayments,
    permission: PERMISSIONS.CREDIT_CARDS,
    sideBar: 'Credit Cards',
    subRoutes: creditCardsSubRoutes,
  },
  {
    path: PATHS.CREDIT_CARDS_PAYMENTS_DETAILS,
    component: CreditCardsPaymentDetails,
    permission: PERMISSIONS.CREDIT_CARDS,
    exact: true,
  },
  {
    path: PATHS.CREDIT_CARDS_DISPUTES_DETAILS,
    component: CreditCardsDisputeDetails,
    permission: PERMISSIONS.CREDIT_CARDS,
    exact: true,
  },
  {
    path: PATHS.CREDIT_CARDS_SUBMIT_EVIDENCE,
    component: CreditCardsSubmitEvidence,
    permission: PERMISSIONS.CREDIT_CARDS,
  },
  {
    path: PATHS.TRANSACTION_DETAILS,
    component: TransactionDetails,
    permission: PERMISSIONS.TRANSACTION,
    exact: true,
  },
  {
    path: PATHS.DISBURSEMENTS,
    component: withBreadcrumbBar(Disbursements),
    permission: PERMISSIONS.DISBURSEMENTS,
    breadcrumbName: getDisbursementFeatureName(),
  },
  {
    path: PATHS.DEPOSIT,
    component: withBreadcrumbBar(Deposit),
    permission: PERMISSIONS.DEPOSIT,
    exact: true,
    breadcrumbName: 'Deposit',
  },
  {
    path: PATHS.DEPOSIT_REQUESTS,
    component: withBreadcrumbBar(DepositRequests),
    permission: PERMISSIONS.DEPOSIT,
    exact: true,
    breadcrumbName: 'Deposit Requests',
  },
  {
    path: PATHS.SIMULATE_DEPOSIT,
    component: withBreadcrumbBar(SimulateDeposit),
    exact: true,
    breadcrumbName: 'Simulate Deposit',
  },
  {
    path: PATHS.WITHDRAW,
    component: withBreadcrumbBar(Withdraw),
    permission: PERMISSIONS.WITHDRAW,
    exact: true,
    breadcrumbName: 'Withdraw',
  },
  {
    path: PATHS.MANAGE_ACCOUNT,
    component: withBreadcrumbBar(MyAccount),
    exact: true,
    breadcrumbName: 'My Account',
  },
  {
    path: PATHS.USERS,
    component: withBreadcrumbBar(MerchantUser),
    permission: PERMISSIONS.MERCHANT_USERS,
    exact: true,
    sideBar: 'Users',
    breadcrumbName: 'Users',
  },
  {
    path: PATHS.USER_DETAILS,
    component: MerchantUserDetails,
    permission: PERMISSIONS.MERCHANT_USERS,
  },
  {
    path: PATHS.DEVELOPER_TOOLS,
    component: withBreadcrumbBar(DeveloperTools),
    permission: PERMISSIONS.DEVELOPER_TOOLS,
    sideBar: 'Developer Tools',
    breadcrumbName: 'Developer Tools',
  },
  {
    path: PATHS.TEAM,
    component: withBreadcrumbBar(Team),
    permission: PERMISSIONS.TEAM,
    sideBar: 'Team',
    breadcrumbName: 'Team',
  },
  {
    path: PATHS.INVOICING,
    component: withBreadcrumbBar(Invoicing),
    permission: PERMISSIONS.INVOICING,
    sideBar: 'Payment Link',
    breadcrumbName: 'Payment Link',
    exact: true,
  },
  {
    path: PATHS.INVOICING_DETAILS,
    component: InvoicingDetails,
    permission: PERMISSIONS.INVOICING,
  },
  {
    path: PATHS.IDENTIFICATION,
    component: withBreadcrumbBar(Identification),
    breadcrumbName: 'Identification',
  },
  {
    path: PATHS.VERIFICATION,
    component:
      ConfigManager.getCountry() === COUNTRY.INDONESIA
        ? withBreadcrumbBar(VerificationIndo)
        : withBreadcrumbBar(NewVerification),
    exact: true,
    breadcrumbName: 'Verification',
    pageTitle: 'Account Manager Verification',
  },
  {
    path: PATHS.BIZ_KYC_INFORMATION,
    component: withBreadcrumbBar(BizKycInformation),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_INTENDED_RELATIONS,
    component: withBreadcrumbBar(BizKycIntendedRelations),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_BUSINESS_ACTIVITIES,
    component: withBreadcrumbBar(BizKycBusinessActivities),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_UPLOAD_DOCUMENTS,
    component: withBreadcrumbBar(BizKycUploadDocuments),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_ACCOUNT_MANAGER,
    component: withBreadcrumbBar(BizKycAccountManager),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_DIRECTORS_AND_BO,
    component: withBreadcrumbBar(BizKycDirectorsAndBo),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_REVIEW,
    component: withBreadcrumbBar(BizKycReview),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.BIZ_KYC_COMPLETE,
    component: withBreadcrumbBar(BizKycComplete),
    breadcrumbName: 'Business Verification',
  },
  {
    path: PATHS.VERIFICATION_FLOW,
    component: withBreadcrumbBar(VerificationFlow),
    exact: true,
    breadcrumbName: 'Verification',
    pageTitle: 'Account Manager Verification',
  },
  {
    path: PATHS.VERIFICATION_MY_INFO_CALLBACK,
    component: withBreadcrumbBar(VerificationMyInfoCallback),
    exact: true,
  },
  {
    path: PATHS.INTERNAL_TOOLS,
    component: withBreadcrumbBar(InternalTools),
    breadcrumbName: 'Internal Tools',
  },
  {
    path: PATHS.ERROR,
    component: Error,
  },
  {
    component: NotFound,
  },
]

const bizRoutes = [...baseRoutes]
const idBizRoutes = [...idSpecificRoutes, ...baseRoutes]
const regionalRoutes = [...creditCardsRoutes, ...baseRoutes]

function getRoutes() {
  if (ConfigManager.isBizSite()) {
    if (ConfigManager.getCountry() === COUNTRY.INDONESIA) return idBizRoutes
    return bizRoutes
  } else if (ConfigManager.isRegionalSite()) {
    return regionalRoutes
  } else {
    return baseRoutes
  }
}

const routes = getRoutes()

export default routes
